import { useState } from "react";
import { CopyOutlined, DeleteOutlined, EditOutlined, LinkOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Space, Switch, Table, Tooltip, Typography } from "antd";
import truncate from "lodash.truncate";
import { useTranslation } from "react-i18next";

import { CheckoutVariantStatus, type SalesFunnel } from "@fitness-app/data-models/entities/SalesFunnel";
import { getSalesFunnelLink } from "@fitness-app/utils/src/products/getProductLink";

import { getPriceFormatter } from "~/modules/Products/utils/getPriceFormatter";
import { useSalesFunnels } from "../hooks/useSalesFunnels";
import { DuplicateSalesFunnelModal } from "./DuplicateSalesFunnelModal";
import { ProductSalesFunnelForm, type SalesFunnelFormModel } from "./ProductSalesFunnelForm";
import { ProductSalesFunnelsDetails } from "./ProductSalesFunnelsDetails";

interface ProductSalesFunnelsProps {
  productId: string;
}

const priceFormatter = getPriceFormatter("PLN");

export const ProductSalesFunnels = ({ productId }: ProductSalesFunnelsProps) => {
  const [excludeTestEvents, setExcludeTestEvents] = useState(false);
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>({});
  const [duplicatedFunnel, setDuplicatedFunnel] = useState<SalesFunnel | null>(null);
  const {
    archiveFunnel,
    data,
    duplicateFunnel,
    editedModel,
    isLoading,
    saveFunnel,
    setEditedItem,
    setEditedModel,
    setShowFormModal,
    showFormModal,
    showSavingIndicator,
    statsByFunnel,
    statsIsLoading,
  } = useSalesFunnels(productId, excludeTestEvents, dateRange);
  const { t } = useTranslation("products");

  const openCreateForm = () => {
    setEditedModel(null);
    setShowFormModal(true);
  };

  const submitFunnel = async (model: SalesFunnelFormModel) => {
    await saveFunnel(model, editedModel?.id);
  };

  return (
    <div>
      <div className="flex justify-end gap-3 py-4">
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateForm}>
          Dodaj nowy sales funnel
        </Button>
      </div>

      <Table<SalesFunnel>
        dataSource={data || []}
        loading={isLoading || statsIsLoading}
        rowKey="id"
        scroll={{ x: true }}
        pagination={{
          hideOnSinglePage: true,
        }}
        expandable={{
          expandedRowRender: (rowData) => {
            return (
              <ProductSalesFunnelsDetails
                funnel={rowData}
                funnelStats={statsByFunnel[rowData.id]}
                excludeTestEvents={excludeTestEvents}
                onExcludeTestEventsChange={setExcludeTestEvents}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
              />
            );
          },
          rowExpandable: (row) => row.steps?.length > 0,
        }}
      >
        <Table.Column<SalesFunnel>
          title="ID"
          dataIndex="id"
          key="id"
          render={(id: string) => (
            <Typography.Paragraph
              className="whitespace-nowrap"
              copyable={{ tooltips: [id, t("common:copied")], text: id }}
              style={{ marginBottom: 0 }}
            >
              {truncate(id, { length: 10 })}
            </Typography.Paragraph>
          )}
        />
        <Table.Column<SalesFunnel> title="Nazwa" key="name" dataIndex="name" />
        <Table.Column<SalesFunnel>
          title="Slug"
          key="slug"
          dataIndex="slug"
          render={(slug: string) => (
            <Typography.Paragraph
              className="whitespace-nowrap"
              copyable={{ tooltips: [slug, t("common:copied")], text: slug }}
              style={{ marginBottom: 0 }}
            >
              {truncate(slug, { length: 10 })}
            </Typography.Paragraph>
          )}
        />
        <Table.Column<SalesFunnel>
          title="Kroki"
          key="steps"
          dataIndex="steps"
          render={(steps: SalesFunnel["steps"]) => steps?.length}
        />
        <Table.Column
          title="Odsłony total"
          key="views"
          dataIndex="views"
          render={(views, row) => {
            const landingViews = statsByFunnel[row.id]?.viewed;

            if (!landingViews) {
              return 0;
            }

            return landingViews;
          }}
        />
        <Table.Column
          title="Ilość tranksacji"
          key="purchase"
          dataIndex="purchase"
          render={(_purchase, row) => {
            const purchase = statsByFunnel[row.id]?.purchased;

            if (!purchase) {
              return 0;
            }

            return purchase;
          }}
        />
        <Table.Column
          title="Przychody"
          key="revenue"
          dataIndex="revenue"
          render={(_rev, row) => {
            const revenue = statsByFunnel[row.id]?.revenue;

            if (!revenue) {
              return priceFormatter.format(0);
            }

            return priceFormatter.format(revenue / 100);
          }}
        />
        <Table.Column
          title="Typ koszyka"
          key="target"
          dataIndex="target"
          render={(target) => t(`checkoutVariants.target.${target}`)}
        />

        <Table.Column<SalesFunnel>
          title={t("list.columns.link")}
          dataIndex="link"
          key="link"
          render={(_, row) => {
            const link = getSalesFunnelLink({
              domain: import.meta.env.VITE_ECOMMERCE_DOMAIN,
              slug: row.slug,
              steps: row.steps,
            });
            return (
              <Tooltip title={t("list.copyToClipboard")}>
                <Button
                  type="link"
                  icon={<LinkOutlined style={{ fontSize: 22 }} />}
                  onClick={() => navigator.clipboard.writeText(link)}
                />
              </Tooltip>
            );
          }}
        />

        <Table.Column<SalesFunnel>
          title={t("list.columns.active")}
          dataIndex="status"
          key="status"
          render={(status: SalesFunnel["status"], row) => {
            return (
              <Switch
                checked={status === CheckoutVariantStatus.Active}
                onChange={(checked) => {
                  void saveFunnel(
                    {
                      activeUntil: row.activeUntil ?? null,
                      cartSettings: row.cartSettings ?? null,
                      default: Boolean(row.default),
                      excluded: Boolean(row.excluded),
                      metadata: row.metadata ?? {},
                      name: row.name,
                      slug: row.slug,
                      status: checked ? CheckoutVariantStatus.Active : CheckoutVariantStatus.Inactive,
                      steps: row.steps,
                      target: row.target,
                    },
                    row.id,
                  );
                }}
              />
            );
          }}
        />
        <Table.Column<SalesFunnel>
          title="Akcje"
          key="actions"
          fixed="right"
          render={(_, row) => (
            <Space>
              <Button icon={<EditOutlined />} onClick={() => setEditedItem(row)}>
                Edytuj
              </Button>
              <Tooltip title="Duplikuj">
                <Button icon={<CopyOutlined />} aria-label="Duplikuj" onClick={() => setDuplicatedFunnel(row)} />
              </Tooltip>
              <Popconfirm
                title="Zarchiwizować sales funnel?"
                okText="Archiwizuj"
                cancelText="Anuluj"
                onConfirm={() => void archiveFunnel(row.id)}
              >
                <Button danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          )}
        />
      </Table>
      <DuplicateSalesFunnelModal
        funnel={duplicatedFunnel}
        isSaving={showSavingIndicator}
        onCancel={() => setDuplicatedFunnel(null)}
        onSubmit={async (funnel, slug) => {
          const result = await duplicateFunnel(funnel, slug);
          if (result === "success") {
            setDuplicatedFunnel(null);
          }
          return result;
        }}
      />
      <ProductSalesFunnelForm
        key={editedModel?.id ?? "new"}
        funnel={editedModel}
        isSaving={showSavingIndicator}
        open={showFormModal}
        productId={productId}
        onCancel={() => {
          setShowFormModal(false);
          setEditedModel(null);
        }}
        onSubmit={submitFunnel}
      />
    </div>
  );
};
